import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';

interface Game {
  _id: string;
  title: string;
  description: string;
  shortDescription: string;
  category: string;
  platform: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  ageRange: string;
  estimatedTime: string;
  gameType: 'Adventure' | 'Puzzle' | 'Competition' | 'Creative' | 'Simulation' | 'Multiplayer';
  isActive: boolean;
  isFeatured: boolean;
  popularity: number;
  rating: number;
  playCount: number;
  completionRate: number;
  averagePlayTime: number;
  bannerImage: string;
  screenshots: string[];
  demoVideo: string;
  tags: string[];
  learningObjectives: string[];
  skills: string[];
  hardware: string[];
  software: string[];
  gameMechanics: string[];
  story: string;
  rules: string[];
  scoring: string;
  progression: {
    levels: number;
    milestones: string[];
    unlockables: string[];
  };
  socialFeatures: {
    multiplayer: boolean;
    sharing: boolean;
    collaboration: boolean;
    competition: boolean;
  };
  accessibility: {
    visual: boolean;
    auditory: boolean;
    motor: boolean;
    cognitive: boolean;
  };
  setup: string[];
  gameplay: string[];
  challenges: {
    beginner: string[];
    intermediate: string[];
    advanced: string[];
  };
  achievements: {
    name: string;
    description: string;
    requirements: string;
    reward: string;
  }[];
  leaderboards: {
    type: string;
    description: string;
  }[];
  resources: {
    tutorials: string[];
    videos: string[];
    documentation: string[];
    community: string[];
  };
  safety: string[];
  troubleshooting: {
    commonIssues: string[];
    solutions: string[];
  };
  extensions: string[];
  realWorldApplications: string[];
  educatorNotes: string;
  parentGuide: string;
  difficultyProgression: {
    level: number;
    requirements: string[];
    rewards: string[];
  }[];
  adaptiveLearning: {
    enabled: boolean;
    algorithm: string;
    personalization: string[];
  };
  analytics: {
    tracking: string[];
    metrics: string[];
    insights: string[];
  };
}

const GameDetail: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const [game, setGame] = useState<Game | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const fetchGame = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL}/highcode/games/${gameId}`);
        if (response.ok) {
          const data = await response.json();
          setGame(data);
        } else {
          setError('Game not found');
        }
      } catch (err) {
        setError('Error fetching game');
      } finally {
        setIsLoading(false);
      }
    };

    if (gameId) {
      fetchGame();
    }
  }, [gameId]);

  if (isLoading) return <LoadingSpinner />;

  if (error || !game) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Game Not Found</h2>
          <p className="text-gray-600 mb-4">{error || 'The game you are looking for does not exist.'}</p>
                 <Link
                   to="/high-code/games"
                   className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                 >
                   Browse All Games
                 </Link>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📋' },
    { id: 'how-to-play', label: 'How to Play', icon: '🎮' },
    { id: 'challenges', label: 'Challenges', icon: '🎯' },
    { id: 'achievements', label: 'Achievements', icon: '🏆' },
    { id: 'resources', label: 'Resources', icon: '📚' },
    { id: 'educator', label: 'For Educators', icon: '👨‍🏫' },
    { id: 'parents', label: 'For Parents', icon: '👨‍👩‍👧‍👦' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      {/* Hero Section */}
      <div className="relative h-96 bg-gradient-to-r from-blue-600 to-purple-600">
        {game.bannerImage && (
          <img
            src={game.bannerImage}
            alt={game.title}
            className="w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-6xl font-bold mb-4">{game.title}</h1>
            <p className="text-xl md:text-2xl mb-6 text-blue-100">{game.shortDescription}</p>
            <div className="flex flex-wrap justify-center gap-4">
              <span className="bg-white/20 px-4 py-2 rounded-full">
                {game.category}
              </span>
              <span className="bg-white/20 px-4 py-2 rounded-full">
                {game.platform}
              </span>
              <span className="bg-white/20 px-4 py-2 rounded-full">
                {game.ageRange}
              </span>
              <span className="bg-white/20 px-4 py-2 rounded-full">
                {game.estimatedTime}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Game Info Bar */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <span className="text-yellow-500">⭐</span>
                <span className="font-semibold">{game.rating.toFixed(1)}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-blue-500">👥</span>
                <span>{game.playCount} plays</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-500">📈</span>
                <span>{game.completionRate}% completion</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {game.socialFeatures.multiplayer && (
                <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">
                  👥 Multiplayer
                </span>
              )}
              {game.socialFeatures.competition && (
                <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs">
                  🏆 Competition
                </span>
              )}
              {game.isFeatured && (
                <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs">
                  ⭐ Featured
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-8">
              <h3 className="text-lg font-semibold mb-4">Game Details</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Difficulty</label>
                  <div className="mt-1">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      game.difficulty === 'Easy' ? 'bg-green-100 text-green-800' :
                      game.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {game.difficulty}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Game Type</label>
                  <p className="text-sm text-gray-900">{game.gameType}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Platform</label>
                  <p className="text-sm text-gray-900">{game.platform}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Age Range</label>
                  <p className="text-sm text-gray-900">{game.ageRange}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Estimated Time</label>
                  <p className="text-sm text-gray-900">{game.estimatedTime}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Skills</label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {game.skills.slice(0, 3).map((skill, index) => (
                      <span key={index} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                        {skill}
                      </span>
                    ))}
                    {game.skills.length > 3 && (
                      <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                        +{game.skills.length - 3}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <Link
                  to={`/high-code/game/${game._id}/play`}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 text-center block"
                >
                  🎮 Start Playing
                </Link>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Tabs */}
            <div className="bg-white rounded-lg shadow-sm mb-6">
              <div className="border-b border-gray-200">
                <nav className="flex overflow-x-auto">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 whitespace-nowrap ${
                        activeTab === tab.id
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <span className="mr-2">{tab.icon}</span>
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>

              <div className="p-6">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-3">About This Game</h3>
                      <p className="text-gray-700">{game.description}</p>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-3">Story</h3>
                      <p className="text-gray-700">{game.story}</p>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-3">Learning Objectives</h3>
                      <ul className="list-disc list-inside space-y-1">
                        {game.learningObjectives.map((objective, index) => (
                          <li key={index} className="text-gray-700">{objective}</li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-3">Game Mechanics</h3>
                      <div className="flex flex-wrap gap-2">
                        {game.gameMechanics.map((mechanic, index) => (
                          <span key={index} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                            {mechanic}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-3">Hardware Required</h3>
                      <ul className="list-disc list-inside">
                        {game.hardware.map((item, index) => (
                          <li key={index} className="text-gray-700">{item}</li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-3">Software Required</h3>
                      <ul className="list-disc list-inside">
                        {game.software.map((item, index) => (
                          <li key={index} className="text-gray-700">{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* How to Play Tab */}
                {activeTab === 'how-to-play' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Setup Instructions</h3>
                      <ol className="list-decimal list-inside space-y-2">
                        {game.setup.map((step, index) => (
                          <li key={index} className="text-gray-700">{step}</li>
                        ))}
                      </ol>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-3">How to Play</h3>
                      <ol className="list-decimal list-inside space-y-2">
                        {game.gameplay.map((step, index) => (
                          <li key={index} className="text-gray-700">{step}</li>
                        ))}
                      </ol>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-3">Game Rules</h3>
                      <ul className="list-disc list-inside space-y-1">
                        {game.rules.map((rule, index) => (
                          <li key={index} className="text-gray-700">{rule}</li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-3">Scoring System</h3>
                      <p className="text-gray-700">{game.scoring}</p>
                    </div>
                  </div>
                )}

                {/* Challenges Tab */}
                {activeTab === 'challenges' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Beginner Challenges</h3>
                      <ul className="list-disc list-inside space-y-1">
                        {game.challenges.beginner.map((challenge, index) => (
                          <li key={index} className="text-gray-700">{challenge}</li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-3">Intermediate Challenges</h3>
                      <ul className="list-disc list-inside space-y-1">
                        {game.challenges.intermediate.map((challenge, index) => (
                          <li key={index} className="text-gray-700">{challenge}</li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-3">Advanced Challenges</h3>
                      <ul className="list-disc list-inside space-y-1">
                        {game.challenges.advanced.map((challenge, index) => (
                          <li key={index} className="text-gray-700">{challenge}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Achievements Tab */}
                {activeTab === 'achievements' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {game.achievements.map((achievement, index) => (
                        <div key={index} className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <h4 className="font-semibold text-yellow-800 mb-2">{achievement.name}</h4>
                          <p className="text-sm text-yellow-700 mb-2">{achievement.description}</p>
                          <div className="text-xs text-yellow-600">
                            <strong>Requirements:</strong> {achievement.requirements}
                          </div>
                          <div className="text-xs text-yellow-600 mt-1">
                            <strong>Reward:</strong> {achievement.reward}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Resources Tab */}
                {activeTab === 'resources' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Tutorials</h3>
                      <ul className="list-disc list-inside space-y-1">
                        {game.resources.tutorials.map((tutorial, index) => (
                          <li key={index} className="text-gray-700">
                            <a href={tutorial} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                              {tutorial}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-3">Videos</h3>
                      <ul className="list-disc list-inside space-y-1">
                        {game.resources.videos.map((video, index) => (
                          <li key={index} className="text-gray-700">
                            <a href={video} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                              {video}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-3">Documentation</h3>
                      <ul className="list-disc list-inside space-y-1">
                        {game.resources.documentation.map((doc, index) => (
                          <li key={index} className="text-gray-700">
                            <a href={doc} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                              {doc}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-3">Community</h3>
                      <ul className="list-disc list-inside space-y-1">
                        {game.resources.community.map((community, index) => (
                          <li key={index} className="text-gray-700">
                            <a href={community} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                              {community}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Educator Tab */}
                {activeTab === 'educator' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Educator Notes</h3>
                      <p className="text-gray-700">{game.educatorNotes}</p>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-3">Learning Objectives</h3>
                      <ul className="list-disc list-inside space-y-1">
                        {game.learningObjectives.map((objective, index) => (
                          <li key={index} className="text-gray-700">{objective}</li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-3">Skills Developed</h3>
                      <div className="flex flex-wrap gap-2">
                        {game.skills.map((skill, index) => (
                          <span key={index} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-3">Real-World Applications</h3>
                      <ul className="list-disc list-inside space-y-1">
                        {game.realWorldApplications.map((application, index) => (
                          <li key={index} className="text-gray-700">{application}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Parents Tab */}
                {activeTab === 'parents' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Parent Guide</h3>
                      <p className="text-gray-700">{game.parentGuide}</p>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-3">Safety Considerations</h3>
                      <ul className="list-disc list-inside space-y-1">
                        {game.safety.map((safety, index) => (
                          <li key={index} className="text-gray-700">{safety}</li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-3">Accessibility Features</h3>
                      <div className="grid grid-cols-2 gap-4">
                        {Object.entries(game.accessibility).map(([feature, enabled]) => (
                          <div key={feature} className="flex items-center">
                            <span className={`w-4 h-4 rounded-full mr-2 ${enabled ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                            <span className="text-sm capitalize">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-3">Troubleshooting</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium text-gray-900">Common Issues</h4>
                          <ul className="list-disc list-inside space-y-1 mt-2">
                            {game.troubleshooting.commonIssues.map((issue, index) => (
                              <li key={index} className="text-gray-700">{issue}</li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">Solutions</h4>
                          <ul className="list-disc list-inside space-y-1 mt-2">
                            {game.troubleshooting.solutions.map((solution, index) => (
                              <li key={index} className="text-gray-700">{solution}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameDetail;
